/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Button, Flex, Menu, MenuButton, MenuGroup, MenuItem, MenuList, Tag } from '@chakra-ui/react';
import moment from 'moment';
import { type FC, useMemo } from 'react';
import CsvDownloadButton from 'react-json-to-csv';
import { copy } from '../../helper';
import { convertCrashToHar } from '../../helper/HarConverter';
import { type Log, type Request } from '../../interface/Crash';
import { type Protocol } from '../../interface/Protocol';
import { ProtocolSessionIdTag } from '../RequestTags';

interface Props {
    protocolId: string;
    details: Protocol;
    sessionIDs: string[];
}

export const Collections: FC<Props> = (props: Props) => {
    const { protocolId, details, sessionIDs } = props;

    const collectionRequest: Record<string, Record<string, Request[]>> = useMemo(
        () => collectRequests(details),
        [details],
    );
    const collectionTransaction: Record<string, object[]> = useMemo(
        () => collectMessageLoggings(details, 'Processing transaction'),
        [details],
    );
    const collectionShare: Record<string, object[]> = useMemo(
        () => collectMessageLoggings(details, 'Processing shares'),
        [details],
    );
    const collectionCommitMessage: Record<string, string> = useMemo(
        () => collectCommitMessages(details, protocolId),
        [details, protocolId],
    );

    return (
        <>
            <Flex gap={6}>
                {Object.keys(collectionRequest).length > 0 && (
                    <Menu>
                        <MenuButton as={Button} colorScheme="blue">
                            HAR download
                        </MenuButton>
                        <MenuList minWidth="240px">
                            {Object.entries(collectionRequest).map(([file, entries]) => {
                                return (
                                    <MenuGroup key={file} title={file}>
                                        {Object.entries(entries).map(([sessionId, requests]) => {
                                            return (
                                                <MenuItem
                                                    key={sessionId}
                                                    value={sessionId}
                                                    onClick={() => {
                                                        protocolToHarConverter(collectionRequest, file, sessionId);
                                                    }}
                                                >
                                                    <ProtocolSessionIdTag index={sessionIDs.indexOf(sessionId)} />
                                                    <Tag variant="solid" bg="lightblue" color="black" ml={3}>
                                                        {`${requests.length} requests`}
                                                    </Tag>
                                                </MenuItem>
                                            );
                                        })}
                                    </MenuGroup>
                                );
                            })}
                        </MenuList>
                    </Menu>
                )}
                {Object.keys(collectionTransaction).length > 0 && (
                    <>
                        <Menu>
                            <MenuButton as={Button} colorScheme="blue">
                                Transactions to CSV
                            </MenuButton>
                            <MenuList minWidth="240px">
                                {Object.entries(collectionTransaction).map(([sessionId, items]) => {
                                    return (
                                        <MenuItem key={sessionId} value={sessionId}>
                                            <CsvDownloadButton
                                                key={sessionId}
                                                data={items.map((e) => splitAmountCurrency(e)) || []}
                                            >
                                                <ProtocolSessionIdTag index={sessionIDs.indexOf(sessionId)} />
                                                <Tag variant="solid" bg="lightblue" color="black" ml={3}>
                                                    {`${items.length} entries`}
                                                </Tag>
                                            </CsvDownloadButton>
                                        </MenuItem>
                                    );
                                })}
                            </MenuList>
                        </Menu>
                        <Menu>
                            <MenuButton as={Button} colorScheme="blue">
                                Transactions JSON
                            </MenuButton>
                            <MenuList minWidth="240px">
                                {Object.entries(collectionTransaction).map(([sessionId, items]) => {
                                    return (
                                        <MenuItem
                                            key={sessionId}
                                            value={sessionId}
                                            onClick={() => {
                                                protocolTransactionsJsonDownload(items);
                                            }}
                                        >
                                            <ProtocolSessionIdTag index={sessionIDs.indexOf(sessionId)} />
                                            <Tag variant="solid" bg="lightblue" color="black" ml={3}>
                                                {`${items.length} entries`}
                                            </Tag>
                                        </MenuItem>
                                    );
                                })}
                            </MenuList>
                        </Menu>
                    </>
                )}
                {Object.keys(collectionShare).length > 0 && (
                    <Menu>
                        <MenuButton as={Button} colorScheme="blue">
                            Shares to CSV
                        </MenuButton>
                        <MenuList minWidth="240px">
                            {Object.entries(collectionShare).map(([sessionId, items]) => {
                                return (
                                    <MenuItem key={sessionId} value={sessionId}>
                                        <CsvDownloadButton
                                            key={sessionId}
                                            data={items.map((e) => splitAmountCurrency(e)) || []}
                                        >
                                            <ProtocolSessionIdTag index={sessionIDs.indexOf(sessionId)} />
                                            <Tag variant="solid" bg="lightblue" color="black" ml={3}>
                                                {`${items.length} entries`}
                                            </Tag>
                                        </CsvDownloadButton>
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </Menu>
                )}
                {Object.keys(collectionCommitMessage).length > 0 && (
                    <Menu>
                        <MenuButton as={Button} colorScheme="blue">
                            Commit Mesage
                        </MenuButton>
                        <MenuList minWidth="240px">
                            {Object.entries(collectionCommitMessage).map(([file, message]) => {
                                return (
                                    <MenuItem
                                        key={file}
                                        value={file}
                                        onClick={() => {
                                            copy(message);
                                        }}
                                    >
                                        {file}
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </Menu>
                )}
            </Flex>
        </>
    );
};

const collectRequests = (details: Protocol): any => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestCollection: any = {};
    details.trace.forEach((item) => {
        const file = item?.body?.file ?? '';
        const sessionId = item['session-id'];
        if (file.trim()) {
            if (file in requestCollection && sessionId in requestCollection[`${file}`]) {
                requestCollection[`${file}`][`${sessionId}`].push(handleBodyObject(item.body!));
            } else if (file in requestCollection && !(sessionId in requestCollection[`${file}`])) {
                requestCollection[`${file}`][`${sessionId}`] = [handleBodyObject(item.body!)];
            } else {
                requestCollection[`${file}`] = {};
                requestCollection[`${file}`][`${sessionId}`] = [handleBodyObject(item.body!)];
            }
        } else {
            // These are logger.infos without any request oder response object
        }
    });
    return requestCollection;
};

const collectMessageLoggings = (details: Protocol, identifier: string): Record<string, object[]> => {
    const requestCollection: Record<string, object[]> = {};
    details.trace.forEach((item) => {
        const sessionId = item['session-id'];

        if (item.message && item.message.includes(identifier)) {
            if (sessionId in requestCollection) {
                requestCollection[sessionId].push(handleTransactionMessage(item.message));
            } else {
                requestCollection[sessionId] = [handleTransactionMessage(item.message)];
            }
        }
    });
    return requestCollection;
};

const collectCommitMessages = (details: Protocol, protocolId: string): any => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const collection: any = {};
    details.trace.forEach((item) => {
        const file = item?.body?.file ?? '';
        const fileVersion = item?.body?.fileVersion ?? '';
        if (file && !(file in collection)) {
            const url = window.location.protocol + '//' + window.location.host;
            // 2025-07-11T07:58:13.825UTC
            const message = `[${file.replace('+', '/').replace('.py', '')}] 
    
<a href='${url}/protocols/${protocolId}' target="_blank">Protocol (${moment(
                item.created,
                'YYYY-MM-DDTHH:mm:ss.SSSUTC',
            ).format('YY-MM-DD HH:mm')})</a>`;
            collection[`${file}`] = message;
        }
    });
    return collection;
};

const handleBodyObject = (body: Log): any => {
    const bindings = body.frames[0].bindings;
    const requests = bindings.requests;
    if (requests) {
        return requests[1];
    }
};

const handleTransactionMessage = (message: string): any => {
    try {
        // regex uses look-forwards and look-behinds to select only single-quotes that should be selected
        const regex = /('(?=(,\s*')))|('(?=:))|((?<=([{:,]\s*))')|((?<={)')|('(?=}))/g;
        const _message = message.replace(regex, '"');
        // eslint-disable-next-line @typescript-eslint/quotes, quotes
        const _string = `{${_message.split('{')[1]}`;
        const data = JSON.parse(_string.trim());

        return data;
    } catch (error) {
        return { error };
    }
};

const splitAmountCurrency = (entry: any): any => {
    const copyEntry = structuredClone(entry);
    const keys = ['amount', 'OriginalAmount', 'price', 'originalValue', 'value', 'percentProfit'];
    for (const key of keys) {
        if (key in copyEntry) {
            const splitValue = copyEntry[key].split(' ');
            copyEntry[key] = parseFloat(splitValue[0]).toLocaleString('de-DE');
            copyEntry[`${key}Currency`] = splitValue[1];
        }
    }
    return copyEntry;
};

const protocolToHarConverter = (collection: Collection, fileName: string, sessionId: string): void => {
    const selectedCollection = collection[`${fileName}`][`${sessionId}`];
    if (selectedCollection) {
        const file = new Blob([JSON.stringify(convertCrashToHar(selectedCollection), null, 4)], { type: 'text/plain' });
        const element = document.createElement('a');
        element.href = URL.createObjectURL(file);
        element.download = `${fileName}@${sessionId}_` + moment().format('YY-MM-DD_HH-mm-ss') + '.har';

        // simulate link click
        document.body.appendChild(element); // Required for this to work in FireFox
        element.click();
        // remove link element
        element?.parentNode?.removeChild(element);
    }
};

const protocolTransactionsJsonDownload = (entries: any): void => {
    const file = new Blob([JSON.stringify(entries, null, 4)], { type: 'text/plain' });
    const element = document.createElement('a');
    element.href = URL.createObjectURL(file);
    element.download = 'export.json';

    // simulate link click
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
    // remove link element
    element?.parentNode?.removeChild(element);
};

type Collection = Record<string, Record<string, Request[]>>;
